// ════════════════════════════════════════════════════════════════════
// Preparar la foto de una firma
//
// Una firma fotografiada en papel llega con el fondo del papel: si se
// guarda tal cual, al imprimirla sale un recuadro blanco tapando la
// línea del documento. Y si la foto tiene sombra, el recuadro sale
// gris.
//
// Aquí se limpia: el papel se vuelve transparente, el trazo se
// conserva, y se recorta al tamaño de la firma. Todo en el navegador,
// sin subir nada a ningún servicio.
// ════════════════════════════════════════════════════════════════════

/** Carga una imagen desde un archivo. */
function cargarImagen(archivo) {
  return new Promise((resolver, rechazar) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolver(img); };
    img.onerror = () => { URL.revokeObjectURL(url); rechazar(new Error('No se pudo abrir la imagen')); };
    img.src = url;
  });
}

/**
 * Umbral de Otsu: separa trazo de papel sin pedirle al usuario que
 * ajuste nada. Busca el corte que deja los dos grupos más separados.
 *
 * Se usa esto y no un valor fijo porque una foto con sombra y otra
 * con flash tienen fondos muy distintos, y un número fijo funciona
 * bien en una y mal en la otra.
 */
function umbralOtsu(histograma, totalPixeles) {
  let sumaTotal = 0;
  for (let i = 0; i < 256; i++) sumaTotal += i * histograma[i];

  let sumaFondo = 0;
  let pesoFondo = 0;
  let mejorVarianza = 0;
  let mejorUmbral = 128;

  for (let t = 0; t < 256; t++) {
    pesoFondo += histograma[t];
    if (pesoFondo === 0) continue;
    const pesoFrente = totalPixeles - pesoFondo;
    if (pesoFrente === 0) break;

    sumaFondo += t * histograma[t];
    const mediaFondo = sumaFondo / pesoFondo;
    const mediaFrente = (sumaTotal - sumaFondo) / pesoFrente;
    const varianza = pesoFondo * pesoFrente * (mediaFondo - mediaFrente) ** 2;

    if (varianza > mejorVarianza) {
      mejorVarianza = varianza;
      mejorUmbral = t;
    }
  }
  return mejorUmbral;
}

/**
 * Convierte la foto de una firma en un PNG con fondo transparente,
 * recortado al trazo.
 *
 * @param {File} archivo
 * @param {object} opciones
 * @param {number} opciones.altoFinal  alto del PNG resultante
 * @param {number} opciones.tolerancia 0 a 1; sube para conservar
 *        trazos muy claros, baja si entra ruido del papel
 */
export async function procesarFotoFirma(archivo, { altoFinal = 200, tolerancia = 0.5 } = {}) {
  const img = await cargarImagen(archivo);

  // Se trabaja a un tamaño manejable: una foto de 12 megapíxeles no
  // aporta nada para una firma y hace lento el análisis.
  const ladoMaximo = 1400;
  const escala = Math.min(1, ladoMaximo / Math.max(img.width, img.height));
  const ancho = Math.max(Math.round(img.width * escala), 1);
  const alto = Math.max(Math.round(img.height * escala), 1);

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;
  const ctx = lienzo.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, ancho, alto);

  const datos = ctx.getImageData(0, 0, ancho, alto);
  const px = datos.data;

  // Luminancia y histograma
  const luz = new Uint8Array(ancho * alto);
  const histograma = new Uint32Array(256);
  for (let i = 0, p = 0; i < px.length; i += 4, p++) {
    const l = Math.round(0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
    luz[p] = l;
    histograma[l]++;
  }

  const umbral = umbralOtsu(histograma, ancho * alto);
  // La tolerancia mueve el corte: más alto conserva trazos claros
  const corte = Math.min(250, Math.round(umbral * (0.75 + tolerancia * 0.5)));

  // El papel se vuelve transparente. El trazo se pinta en tinta
  // oscura, con opacidad según lo oscuro que estuviera: así los bordes
  // quedan suaves en lugar de dentados.
  let minX = ancho; let minY = alto; let maxX = -1; let maxY = -1;
  for (let p = 0, i = 0; p < luz.length; p++, i += 4) {
    const l = luz[p];
    if (l >= corte) {
      px[i + 3] = 0;
      continue;
    }
    const fuerza = Math.min(1, (corte - l) / Math.max(corte * 0.6, 1));
    px[i] = 20; px[i + 1] = 25; px[i + 2] = 35;
    px[i + 3] = Math.round(Math.min(255, 90 + fuerza * 165));

    const x = p % ancho;
    const y = (p / ancho) | 0;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  if (maxX < 0) {
    throw new Error('No se distingue ninguna firma en la foto. '
                  + 'Prueba con más luz y sobre papel claro.');
  }

  ctx.putImageData(datos, 0, 0);

  // Recortar al trazo, con un margen para que no quede pegado al borde
  const margen = Math.round(Math.max(maxX - minX, maxY - minY) * 0.04) + 4;
  const rx = Math.max(minX - margen, 0);
  const ry = Math.max(minY - margen, 0);
  const rw = Math.min(maxX + margen, ancho - 1) - rx + 1;
  const rh = Math.min(maxY + margen, alto - 1) - ry + 1;

  const proporcionOcupada = (rw * rh) / (ancho * alto);
  const salida = document.createElement('canvas');
  const anchoFinal = Math.round((rw / rh) * altoFinal);
  salida.width = Math.max(anchoFinal, 1);
  salida.height = altoFinal;
  salida.getContext('2d').drawImage(lienzo, rx, ry, rw, rh, 0, 0, salida.width, salida.height);

  const base64 = salida.toDataURL('image/png').split(',')[1];

  return {
    base64,
    ancho: salida.width,
    alto: salida.height,
    // Avisos para que la persona sepa si la foto sirve o conviene repetirla
    aviso: proporcionOcupada > 0.85
      ? 'La firma ocupa casi toda la foto: puede que se haya colado el fondo. '
        + 'Revisa la vista previa.'
      : proporcionOcupada < 0.01
        ? 'La firma salió muy pequeña en la foto. Acércate más para que se vea nítida.'
        : null,
  };
}
